
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { CalendarSettings } from "@/components/settings/CalendarSettings";
import { EmailSettings } from "@/components/settings/EmailSettings";
import { APISettings } from "@/components/settings/APISettings";
import { IntegrationsGrouping } from "./IntegrationsGrouping";
import MCPServerConfig from "./MCPServerConfig";
import { ElevenLabsScreeningSetup } from "./ElevenLabsScreeningSetup";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { OutlookEmailConnectButton } from "@/components/settings/outlook/OutlookEmailConnectButton";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { getErrorMessage } from "@/utils/errorHandling";

interface IntegrationsTabsProps {
  isAdmin: boolean;
}

export const IntegrationsTabs = ({ isAdmin }: IntegrationsTabsProps) => {
  const [activeTab, setActiveTab] = useState("overview");
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const tabParam = searchParams.get("tab");
    const code = searchParams.get("code");
    const state = searchParams.get("state");

    // Handle Outlook Email OAuth Callback
    const pendingFlow = localStorage.getItem('oauth_pending_flow');
    if (code && state && pendingFlow === 'outlook_email_sync') {
      localStorage.removeItem('oauth_pending_flow');
      const originalRedirectUri = localStorage.getItem('oauth_redirect_uri') || '/integrations?tab=communication';
      localStorage.removeItem('oauth_redirect_uri');

      console.log("Handling Outlook Email OAuth callback with code:", code);
      toast.info("Processing Outlook connection...");

      const processOutlookCode = async () => {
        try {
          const session = await supabase.auth.getSession();
          const accessToken = session.data.session?.access_token;
          if (!accessToken) {
            throw new Error("User not authenticated to finalize Outlook connection.");
          }

          const response = await fetch(
            `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/outlook-email-oauth`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${accessToken}`,
              },
              body: JSON.stringify({ code, state }),
            }
          );

          if (!response.ok) {
            const errorData = await response.json().catch(() => ({ error: "Failed to parse error from server" }));
            throw new Error(errorData.error || `Server error: ${response.status}`);
          }

          const result = await response.json();
          toast.success(result.message || "Outlook account connected successfully for email sync!");
        } catch (error) {
          console.error("Error processing Outlook OAuth code:", error);
          toast.error(`Failed to connect Outlook account: ${getErrorMessage(error)}`);
        } finally {
          const newSearchParams = new URLSearchParams(searchParams);
          newSearchParams.delete('code');
          newSearchParams.delete('state');
          newSearchParams.delete('session_state');

          const targetPath = new URL(window.location.origin + originalRedirectUri);
          const existingPathParams = new URLSearchParams(targetPath.search);
          existingPathParams.delete('code');
          existingPathParams.delete('state');
          existingPathParams.delete('session_state');

          navigate(`${targetPath.pathname}?${existingPathParams.toString()}`, { replace: true });
        }
      };
      processOutlookCode();
    }

    if (tabParam && !code) {
      if (["linkedin", "indeed", "dice-1", "zip"].includes(tabParam)) {
        setActiveTab("recruiting");
      } else if (["openai", "anthropic", "gemini", "mistral"].includes(tabParam)) {
        setActiveTab("ai");
      } else if (["elevenlabs", "screening", "phone"].includes(tabParam)) {
        setActiveTab("screening");
      } else if (["communication", "outlook", "email"].includes(tabParam)) {
        setActiveTab("communication");
      } else if (["google-calendar", "ms-calendar", "calendar"].includes(tabParam)) {
        setActiveTab("calendar");
      } else if (["scrapeless", "resend"].includes(tabParam)) {
        setActiveTab("tools");
      } else {
        setActiveTab(tabParam);
      }
    }
  }, [location, navigate]);

  return (
    <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
      <TabsList className="mb-4">
        <TabsTrigger value="overview">Overview</TabsTrigger>
        <TabsTrigger value="mcp">MCP Servers</TabsTrigger>
        <TabsTrigger value="communication">Email</TabsTrigger>
        <TabsTrigger value="calendar">Calendar & Scheduling</TabsTrigger>
        <TabsTrigger value="screening">Phone Screening</TabsTrigger>
        <TabsTrigger value="recruiting">Recruiting</TabsTrigger>
        <TabsTrigger value="ai">AI Services</TabsTrigger>
        <TabsTrigger value="tools">Tools & Utilities</TabsTrigger>
        <TabsTrigger value="all">All Integrations</TabsTrigger>
      </TabsList>

      <TabsContent value="overview">
        <IntegrationsGrouping />
      </TabsContent>

      <TabsContent value="mcp">
        <div className="space-y-8">
          <div>
            <h2 className="text-2xl font-bold mb-4">MCP Server Connections</h2>
            <p className="text-muted-foreground mb-6">
              Configure Model Context Protocol servers to enable enhanced AI agent capabilities including voice synthesis, social media integration, and advanced automation.
            </p>
          </div>
          <MCPServerConfig />
        </div>
      </TabsContent>

      <TabsContent value="communication">
        <div className="space-y-8">
          <div>
            <h2 className="text-2xl font-bold mb-4">Email Integration</h2>
            <p className="text-muted-foreground mb-6">
              Configure email settings and connect your email accounts for synchronization.
            </p>
          </div>
          <EmailSettings />
          <OutlookEmailConnectButton />
        </div>
      </TabsContent>

      <TabsContent value="calendar">
        <div className="space-y-8">
          <div>
            <h2 className="text-2xl font-bold mb-4">Calendar & Scheduling</h2>
            <p className="text-muted-foreground mb-6">
              Connect your calendar services to sync events and manage scheduling.
            </p>
          </div>
          <CalendarSettings />
        </div>
      </TabsContent>

      <TabsContent value="screening">
        <div className="space-y-8">
          <div>
            <h2 className="text-2xl font-bold mb-4">Phone Screening Setup</h2>
            <p className="text-muted-foreground mb-6">
              Configure ElevenLabs for on-demand phone screening where candidates can call anytime.
            </p>
          </div>
          <ElevenLabsScreeningSetup />
        </div>
      </TabsContent>

      <TabsContent value="recruiting">
        <APISettings categoryFilter="recruiting" />
      </TabsContent>

      <TabsContent value="ai">
        <APISettings categoryFilter="ai" />
      </TabsContent>

      <TabsContent value="tools">
        <APISettings categoryFilter="tools" />
      </TabsContent>

      <TabsContent value="all">
        <APISettings />
      </TabsContent>
    </Tabs>
  );
};
